import React, { useCallback, useMemo, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import RediscoveryFilterRow from 'talent_matching/talent_rediscovery/components/filters/rediscovery_filter_row';
import RediscoveryTableWrapper from 'talent_matching/talent_rediscovery/components/table/rediscovery_table_wrapper';
import {
  TalentRediscoveryFilters,
  TalentSearchQueryParams,
  type SavedSearch,
} from 'talent_search/shared/types';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import type { AdvancedFilterType } from 'talent_matching/types';
import {
  toSavedSearchQueryString,
  extractFormFieldsFromSavedSearch,
} from 'talent_search/lib/query_params';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import CustomColumnSelector from 'talent_matching/components/shared/custom_column_selector';
import { useRediscoveryColumns } from 'talent_matching/talent_rediscovery/components/table/rediscovery_columns';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import {
  ACTIONS_COLUMN_FIELD,
  useColumnVisibility,
  NON_TOGGLABLE_COLUMNS,
} from 'talent_matching/hooks/use_column_visibility';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import SavedFilters from 'talent_matching/components/filters/saved_filters';
import { useSavedFilters } from 'talent_matching/hooks/use_saved_filters';

const REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL: GridColumnVisibilityModel = {
  fullName: true,
  latestApplicationHistory: true,
  summary: true,
  tags: false,
  [ACTIONS_COLUMN_FIELD]: true,
};

const t = withLocalePrefix('talent_matching.header_row');

export const DEFAULT_DIRTY_FIELDS = ['exclude_hired', 'no_strong_no_scores'];

export type RediscoveryPageContentProps = {
  defaultValues: TalentRediscoveryFilters;
};

const RediscoveryPageContent = ({
  defaultValues,
}: RediscoveryPageContentProps) => {
  const {
    savedSearchOptions,
    setSavedSearchOptions,
    savedFilterSet,
    setSavedFilterSet,
  } = useRediscoveryTabContext();

  const {
    getValues,
    control,
    formState: { dirtyFields },
  } = useFormContext();
  const userDateFormat = useWatch({ control, name: 'user_date_format' }) ?? '';

  const [filteredTotalRows, setFilteredTotalRows] = useState<number>(0);

  const [normalizedColumnVisibilityModel, setColumnVisibilityModel] =
    useColumnVisibility({
      storageKey: 'talent-rediscovery-column-visibility',
      defaults: REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL,
    });

  const parseSavedSearch = useCallback(
    (response: unknown) =>
      extractFormFieldsFromSavedSearch(
        response as SavedSearch,
        userDateFormat
      ) as Record<string, unknown>,
    [userDateFormat]
  );

  const buildQueryString = useCallback(() => {
    const formValues = getValues();
    formValues.advancedFilters = (formValues.advancedFilters || []).filter(
      (filter: AdvancedFilterType) => new AdvancedFilter(filter).applied()
    );
    return toSavedSearchQueryString(formValues as TalentSearchQueryParams);
  }, [getValues]);

  const { savedFiltersProps, handleClearAll } = useSavedFilters({
    featureType: 'talent_matching_rediscovery',
    formDefaults: defaultValues as Record<string, unknown>,
    savedFilterSet,
    setSavedFilterSet,
    savedSearchOptions,
    setSavedSearchOptions,
    parseSavedSearch,
    buildQueryString,
  });

  const hasAnyFilters =
    Object.keys(dirtyFields).filter(
      (key) => !DEFAULT_DIRTY_FIELDS.includes(key)
    ).length > 0;

  const columns = useRediscoveryColumns({ userDateFormat });
  const columnSelectorColumns = useMemo(() => {
    return columns
      .filter((c) => !NON_TOGGLABLE_COLUMNS.includes(c.field))
      .map((c) => ({
        field: c.field,
        label: c.headerName || c.field,
      }));
  }, [columns]);

  return (
    <Stack spacing={0}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginTop: 0, marginBottom: 2 }}
      >
        <Body medium>{t('candidates', { count: filteredTotalRows })}</Body>
        <CustomColumnSelector
          columns={columnSelectorColumns}
          columnVisibilityModel={normalizedColumnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          defaultColumnVisibilityModel={
            REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL
          }
        />
      </Stack>
      <RediscoveryFilterRow
        onClearAll={handleClearAll}
        formDefaults={defaultValues as Record<string, unknown>}
      >
        <SavedFilters {...savedFiltersProps} />
      </RediscoveryFilterRow>

      <RediscoveryTableWrapper
        onTotalRowsChange={setFilteredTotalRows}
        columnVisibilityModel={normalizedColumnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        hasAnyFilters={hasAnyFilters}
        onClearAll={handleClearAll}
      />
    </Stack>
  );
};

export default RediscoveryPageContent;
